import { useState, type ReactNode } from 'react';
import { Modal } from './Modal.js';

/** Radio choices as large cards, each with a line saying what it means (F10). */
export function ChoiceList<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string; description: string }[];
  onChange: (value: T) => void;
}) {
  const id = `choices-${label.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <>
      <div className="field-label" id={id} style={{ marginTop: 14 }}>
        {label}
      </div>
      <div role="radiogroup" aria-labelledby={id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {options.map((t) => {
          const on = value === t.value;
          return (
            <button
              key={t.value}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onChange(t.value)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '11px 14px',
                border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                background: on ? 'var(--accent-soft)' : 'var(--bg-surface)',
                borderRadius: 8,
                textAlign: 'left',
                cursor: 'pointer',
                color: 'var(--text-1)',
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 18,
                  height: 18,
                  marginTop: 1,
                  borderRadius: 99,
                  border: `2px solid ${on ? 'var(--accent)' : 'var(--border-strong, var(--border))'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 'none',
                  boxSizing: 'border-box',
                }}
              >
                {on && <span style={{ width: 8, height: 8, borderRadius: 99, background: 'var(--accent)' }} />}
              </span>
              <span>
                <span style={{ display: 'block', fontWeight: 800, fontSize: 14 }}>{t.label}</span>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-2)' }}>{t.description}</span>
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}

/** An on/off switch with a label and a line under it. */
export function Switch({
  on,
  label,
  help,
  disabled,
  onChange,
}: {
  on: boolean;
  label: string;
  help?: ReactNode;
  disabled?: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        disabled={disabled}
        className={'toggle' + (on ? ' on' : '')}
        onClick={() => onChange(!on)}
        style={{ flex: 'none', opacity: disabled ? 0.5 : 1 }}
      />
      <div>
        <div style={{ fontWeight: 800, fontSize: 13.5 }}>{label}</div>
        {help && (
          <div className="field-help" style={{ margin: 0 }}>
            {help}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * An in-app confirm (F10: instead of `window.confirm`). `onConfirm` may throw
 * to keep the dialog open; its message shows under the text.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  danger,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const confirm = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onConfirm();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={open} onClose={() => !busy && onCancel()} width={440}>
      <div role="alertdialog" aria-label={title} style={{ padding: 22 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>{title}</h3>
        <div style={{ fontSize: 13.5, color: 'var(--text-2)', marginTop: 8 }}>{body}</div>
        {err && <div style={{ marginTop: 10, fontSize: 13, color: 'var(--st-declined)' }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9, marginTop: 18 }}>
          <button className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button className={danger ? 'btn danger' : 'btn primary'} onClick={() => void confirm()} disabled={busy} autoFocus>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}
